import { useMemo } from "react";
import { useDispatch } from "react-redux";
import { openModal } from "risa-oasis-ui_v2";
import CustomTable from "../../../../components/custom-table/custom-table";
import { LoaderMessage } from "../../../../components/loaderMessage/loaderMessage";
import { OrganizationType } from "../../../../enums/organizationTypes";
import { setFilter } from "../../../../redux/slice/filterValuesSlice";
import AddMedicationModal, {
  ADD_MEDICATION_MODAL_ID,
} from "./components/AddMedicationModal";
import MedicationListFooter from "./components/MedicationListFooter";
import { useMedicationFilters } from "./hooks/useMedicationFilters";
import { useMedicationList } from "./hooks/useMedicationList";
import {
  buildFilterSections,
  buildTableHeaders,
  buildTableRows,
  filterValuesToMedicationFilters,
} from "./utils/buildTableData";

const MEDICATION_TABLE_NAME = "medication-list-table";
const ROWS_PER_PAGE = 10;

const MedicationList = () => {
  const dispatch = useDispatch();
  const list = useMedicationList(OrganizationType.NYCBS_DRUG_CONFIG);
  const filters = useMedicationFilters(list.medications);

  const tableHeaders = useMemo(() => buildTableHeaders(), []);
  const tableData = useMemo(
    () => buildTableRows(filters.filteredRows, list.isDirty),
    [filters.filteredRows, list.isDirty],
  );
  const filterSections = useMemo(() => buildFilterSections(), []);

  const isFilterApplied =
    filters.search.trim().length > 0 ||
    filters.filters.classes.length > 0 ||
    filters.filters.cohorts.length > 0 ||
    filters.filters.sources.length > 0;

  // const handleOrgChange = (next: string) => {
  //   if (list.dirtyCount > 0) {
  //     const ok = window.confirm(
  //       "You have unsaved changes. Switching organization will discard them. Continue?",
  //     );
  //     if (!ok) return;
  //   }
  //   list.setOrganization(next);
  // };

  /**
   * Fields that are stored as a single string in the model but displayed via
   * EDITABLE_TAG_LIST (which returns string[]). We must unwrap the array back
   * to a scalar before persisting.
   */
  const SINGLE_STRING_TAG_FIELDS = new Set([
    "name_replacement",
    "replacement_name",
  ]);

  /**
   * Generic dispatch: every editable cell type calls this with the row's full
   * shaped data, the column key, and the new value. Column keys map 1:1 to
   * medication fields so the patch is a single splat.
   */
  const handleCellChange = (rowData: any, fieldKey: string, newValue: any) => {
    if (!rowData?.name || !fieldKey || fieldKey === "drug") return;

    const value =
      SINGLE_STRING_TAG_FIELDS.has(fieldKey) && Array.isArray(newValue)
        ? newValue[newValue.length - 1] || ""
        : newValue;

    list.updateRow(rowData.name, { [fieldKey]: value });
  };

  /**
   * Two writes per filter apply: (1) push raw FilterValues into the shared
   * filter slice so FilterTag chips and the filter popup pre-selection can
   * read it; (2) translate into our hook's typed shape for the actual filter.
   */
  const handleFilterApply = (data: any) => {
    dispatch(setFilter(data));
    filters.setFilters(filterValuesToMedicationFilters(data));
  };

  return (
    <div className="medication-list h-full w-full bg-primaryGray-16 p-4">
      <div className="medication-list__inner-container flex h-full flex-col gap-4 overflow-hidden rounded-lg bg-white p-6">
        <div className="medication-list__header border-b border-primaryGray-16 pb-4">
          <div className="text-h11 font-bold text-primaryGray-1">
            Medication List
          </div>
          <div className="mt-1 text-small text-primaryGray-4">
            Manage and configure the medication list for prior authorization
            workflows
          </div>
        </div>

        {/* <MedicationListToolbar
          organization={list.organization}
          onOrganizationChange={handleOrgChange}
        /> */}

        <div className="medication-list__table flex-1 overflow-auto">
          {list.loading ? (
            <div className="flex h-full items-center justify-center">
              <LoaderMessage message="Loading medication list…" />
            </div>
          ) : (
            <CustomTable
              tableName={MEDICATION_TABLE_NAME}
              tableHeaders={tableHeaders}
              tableData={tableData}
              count={tableData.length}
              totalCount={list.medications.length}
              itemsPerPage={ROWS_PER_PAGE}
              pagesPerView={5}
              isFetching={false}
              placeholder="Search by drug name, related drug…"
              searchingText={filters.setSearch}
              showFilterButton
              filterSectionsData={filterSections}
              onFilterApplyClicked={handleFilterApply}
              showRefreshButton
              onRefreshButton={list.refresh}
              showOtherButton
              otherButtonMeta={[
                {
                  text: "+ Add Medication",
                  onClick: () => openModal(ADD_MEDICATION_MODAL_ID),
                  disabled: list.saving,
                  buttonType: "primary",
                  size: "medium",
                },
              ]}
              onCellValueChange={handleCellChange}
              isFilterApplied={isFilterApplied}
              disableHoverAndClick
            />
          )}
        </div>

        <MedicationListFooter
          lastSync={list.lastSync}
          dirtyCount={list.dirtyCount}
          saving={list.saving}
          onSave={list.saveChanges}
        />
      </div>
      <AddMedicationModal
        hasNameInList={list.hasNameInList}
        onConfirm={list.appendMedication}
      />
    </div>
  );
};

export default MedicationList;
